/**
 * TeamSystem Impact - Clone Year Dialog
 * Dialog for cloning year data with selective options
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Checkbox,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ListAlt as IROIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  CheckCircle as CheckIcon,
  FolderCopy as DocumentsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { setupApi } from '../api';
import { useWorkingYear } from '../../../store/workingYearStore';

// I18n labels
function useLabels() {
  const { t } = useTranslation();

  return {
    cloneYear: t('dma.cloneYear.cloneYear'),
    sourceYear: t('dma.cloneYear.sourceYear'),
    targetYear: t('dma.cloneYear.targetYear'),
    selectData: t('dma.cloneYear.selectData'),
    dmaSetup: t('dma.cloneYear.dmaSetup'),
    dmaSetupDesc: t('dma.cloneYear.dmaSetupDesc'),
    iros: t('dma.cloneYear.iros'),
    irosDesc: t('dma.cloneYear.irosDesc'),
    assessments: t('dma.cloneYear.assessments'),
    assessmentsDesc: t('dma.cloneYear.assessmentsDesc'),
    campaigns: t('dma.cloneYear.campaigns'),
    campaignsDesc: t('dma.cloneYear.campaignsDesc'),
    documents: t('dma.cloneYear.documents'),
    documentsDesc: t('dma.cloneYear.documentsDesc'),
    externalStakeholders: t('dma.cloneYear.externalStakeholders'),
    externalStakeholdersDesc: t('dma.cloneYear.externalStakeholdersDesc'),
    internalStakeholders: t('dma.cloneYear.internalStakeholders'),
    internalStakeholdersDesc: t('dma.cloneYear.internalStakeholdersDesc'),
    clone: t('dma.cloneYear.clone'),
    cancel: t('dma.cloneYear.cancel'),
    cloning: t('dma.cloneYear.cloning'),
    alwaysIncluded: t('dma.cloneYear.alwaysIncluded'),
    globalData: t('dma.cloneYear.globalData'),
    success: t('dma.cloneYear.success'),
    done: t('dma.cloneYear.done'),
    skippedTitle: t('dma.cloneYear.skippedTitle'),
    skippedHint: t('dma.cloneYear.skippedHint'),
  };
}

interface CloneOption {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
  isGlobal?: boolean;
  alwaysIncluded?: boolean;
}

interface CloneYearDialogProps {
  open: boolean;
  onClose: () => void;
  sourceYear: number;
  onSuccess: () => void;
}

export function CloneYearDialog({
  open,
  onClose,
  sourceYear,
  onSuccess,
}: CloneYearDialogProps) {
  const labels = useLabels();
  const { addYear, setYear } = useWorkingYear();
  const [targetYear, setTargetYear] = useState(sourceYear + 1);
  const [cloning, setCloning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [skippedIros, setSkippedIros] = useState<Array<{ code: string; reason: string }> | null>(null);

  const [options, setOptions] = useState<CloneOption[]>([
    {
      id: 'dmaSetup',
      label: labels.dmaSetup,
      description: labels.dmaSetupDesc,
      icon: <SettingsIcon />,
      enabled: true,
      alwaysIncluded: true,
    },
    {
      id: 'iros',
      label: labels.iros,
      description: labels.irosDesc,
      icon: <IROIcon />,
      enabled: true,
    },
    {
      id: 'assessments',
      label: labels.assessments,
      description: labels.assessmentsDesc,
      icon: <CheckIcon />,
      enabled: false,
    },
    {
      id: 'campaigns',
      label: labels.campaigns,
      description: labels.campaignsDesc,
      icon: <CampaignIcon />,
      enabled: false,
    },
    {
      id: 'documents',
      label: labels.documents,
      description: labels.documentsDesc,
      icon: <DocumentsIcon />,
      enabled: false,
    },
    {
      id: 'externalStakeholders',
      label: labels.externalStakeholders,
      description: labels.externalStakeholdersDesc,
      icon: <PeopleIcon />,
      enabled: false,
      isGlobal: true,
    },
    {
      id: 'internalStakeholders',
      label: labels.internalStakeholders,
      description: labels.internalStakeholdersDesc,
      icon: <BusinessIcon />,
      enabled: false,
      isGlobal: true,
    },
  ]);

  const handleToggleOption = (id: string) => {
    setOptions(prev => {
      const next = prev.map(opt =>
        opt.id === id && !opt.alwaysIncluded
          ? { ...opt, enabled: !opt.enabled }
          : opt
      );
      // Dipendenza: "copia valutazioni" ha senso solo se si clonano gli IRO.
      const irosOn = next.find(o => o.id === 'iros')?.enabled ?? false;
      const assessmentsOn = next.find(o => o.id === 'assessments')?.enabled ?? false;
      return next.map(opt => {
        // Se spengo gli IRO, spengo anche le valutazioni.
        if (opt.id === 'assessments' && !irosOn) return { ...opt, enabled: false };
        // Se accendo le valutazioni, accendo anche gli IRO.
        if (opt.id === 'iros' && assessmentsOn) return { ...opt, enabled: true };
        return opt;
      });
    });
  };

  const handleClone = async () => {
    try {
      setCloning(true);
      setError(null);
      setProgress(10);

      // Build options object
      const irosEnabled = options.find(o => o.id === 'iros')?.enabled || false;
      const cloneOptions = {
        dmaSetup: true, // Always true
        iros: irosEnabled,
        // Le valutazioni si copiano solo se si clonano anche gli IRO.
        assessments: irosEnabled && (options.find(o => o.id === 'assessments')?.enabled || false),
        campaigns: options.find(o => o.id === 'campaigns')?.enabled || false,
        documents: options.find(o => o.id === 'documents')?.enabled || false,
        externalStakeholders: options.find(o => o.id === 'externalStakeholders')?.enabled || false,
        internalStakeholders: options.find(o => o.id === 'internalStakeholders')?.enabled || false,
      };

      setProgress(30);

      // Call clone API
      const result = await setupApi.cloneYear(sourceYear, targetYear, cloneOptions);

      setProgress(100);

      // Se alcuni IRO sono stati saltati, mostrali e lascia il dialog aperto così
      // l'utente li può leggere. NON cambiare anno di lavoro adesso: farlo
      // ricaricherebbe la pagina e chiuderebbe il dialog prima della lettura.
      // Il cambio anno avviene quando l'utente clicca "Fatto" (handleDone).
      if (result.irosSkipped.length > 0) {
        setSkippedIros(result.irosSkipped);
      } else {
        // Nessun IRO saltato: aggiungi/switcha anno e chiudi subito.
        addYear(targetYear);
        setYear(targetYear);
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCloning(false);
    }
  };

  // Chiamata dal pulsante "Fatto" quando ci sono stati IRO saltati:
  // ora è sicuro cambiare anno di lavoro e chiudere il dialog.
  const handleDone = () => {
    addYear(targetYear);
    setYear(targetYear);
    onSuccess();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{labels.cloneYear}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {skippedIros && skippedIros.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {labels.skippedTitle}
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 1, mt: 0 }}>
                {skippedIros.map((s) => (
                  <li key={s.code}>
                    <Typography variant="body2">
                      <strong>{s.code}</strong>
                    </Typography>
                  </li>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {labels.skippedHint}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label={labels.sourceYear}
              value={sourceYear}
              disabled
              size="small"
              sx={{ width: 120 }}
            />
            <Typography sx={{ alignSelf: 'center' }}>→</Typography>
            <TextField
              label={labels.targetYear}
              type="number"
              value={targetYear}
              onChange={(e) => setTargetYear(parseInt(e.target.value))}
              size="small"
              sx={{ width: 120 }}
            />
          </Box>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {labels.selectData}
          </Typography>

          <List dense>
            {options.map(option => (
              <ListItem
                key={option.id}
                role="button"
                tabIndex={0}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: option.enabled ? 'primary.50' : 'transparent',
                  cursor: option.alwaysIncluded ? 'default' : 'pointer',
                  '&:hover': {
                    bgcolor: option.alwaysIncluded ? 'primary.50' : 'action.hover',
                  },
                }}
                onClick={() => !option.alwaysIncluded && handleToggleOption(option.id)}
                onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!option.alwaysIncluded) handleToggleOption(option.id); } }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={option.enabled}
                    disabled={option.alwaysIncluded}
                    onChange={() => handleToggleOption(option.id)}
                  />
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 40, color: option.enabled ? 'primary.main' : 'text.secondary' }}>
                  {option.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.label}
                      {option.alwaysIncluded && (
                        <Chip size="small" label={labels.alwaysIncluded} color="primary" variant="outlined" />
                      )}
                      {option.isGlobal && (
                        <Chip size="small" label={labels.globalData} variant="outlined" />
                      )}
                    </Box>
                  }
                  secondary={option.description}
                />
                {option.enabled && <CheckIcon color="success" />}
              </ListItem>
            ))}
          </List>

          {cloning && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {labels.cloning}
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {skippedIros ? (
          <Button variant="contained" onClick={handleDone}>
            {labels.done}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={cloning}>{labels.cancel}</Button>
            <Button
              variant="contained"
              onClick={handleClone}
              disabled={cloning}
            >
              {labels.clone}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
